// Types
interface ProfileBanner {
  headline: string;
  profileSummary: string;
  credentials: string[];
  expertise: string[];
  resumeLink: { url: string };
  linkedinLink: string;
  backgroundImage: { url: string };
  callToAction: string;
}

interface ContactInfo {
  name: string;
  title: string;
  summary: string;
  companyUniversity: string;
  linkedinLink: string;
  email: string;
  phoneNumber: string;
  profilePicture: { url: string };
}


interface Skill {
  name: string;
  description: string;
  logo: string;
}

interface Skills {
  technical: Skill[];
  domain: Skill[];
  tools: Skill[];
  qualifications: Skill[];
  softSkills: Skill[];
}

interface Project {
  title: string;
  description: string;
  technologies: string[];
  imageUrl: string;
  githubLink?: string;
  liveLink?: string;
}



interface Certification {
  title: string;
  provider: string;
  date: string;
  credentialUrl: string;
  type: string;
  importance: number;
}

interface PortfolioData {
  profileBanner: ProfileBanner;
  contactInfo: ContactInfo;
  
  skills: Skills;
  projects: Project[];

  certifications: Certification[];
  trending: {
    title: string;
    description: string;
    category: 'leadership' | 'project' | 'publication';
    image: string;
    link?: string;
    date: string;
  }[];
  topRated: {
    title: string;
    category: 'project' | 'testimonial' | 'publication' | 'achievement';
    description: string;
    impact?: string[];
    image: string;
    link?: string;
    rating: number;
    year: string;
  }[];
}

interface TimelineData {
  work: TimelineItem[];
  education: TimelineItem[];
}

interface TimelineItem {
  title: string;
  company: string;
  location: string;
  period: string;
  type: string;
  technologies: string[];
  achievements: string[];
  icon: string;
}

// Portfolio Data Configuration
export const portfolioData: PortfolioData = {
  // Profile Banner Data
  profileBanner: {
    headline: "Uchenna Anyaoha - AI/ML Engineering",
    profileSummary: "AI/ML engineering leader with over 10 years of experience across AI strategy, machine learning, data science and enterprise platform architecture. Specializing in production LLM systems, enterprise RAG pipelines and scalable ML infrastructure.",
    credentials: [
      "Professional Engineer (P.Eng)",
      "Project Management Professional (PMP)",
      "Published Researcher",
      "Founder & Advisor, SinewsLink"
    ],
    expertise: [
      "AI Strategy & Leadership",
      "LLMOps & RAG",
      "Machine Learning & Data Engineering",
      "Cloud Architecture"
    ],
    resumeLink: {
      url: "/files/UCHENNA_ANYAOHA_Resume.pdf"
    },
    linkedinLink: "https://www.linkedin.com/in/uchenna-e-anyaoha/",
    backgroundImage: {
      url: "src/images/anyaoha.gif"
    },
    callToAction: "Resume"
  },

  // Contact Information
  contactInfo: {
    name: "U. Anyaoha",
    title: "AI/ML Engineering Leader",
    summary: "With over 10+ years of experience, I'm passionate about creating efficient, scalable business solutions via data, machine learning and AI",
    companyUniversity: "SinewsLink",
    linkedinLink: "https://www.linkedin.com/in/uchenna-e-anyaoha/",
    email: "uche@ucheanyaoha.com",
    phoneNumber: "+1 250 575 47",
    profilePicture: {
      url: "/images/anyaoha.gif"
    }
  },

  // Recommendations
  

  // Skills Data
  skills: {
    technical: [
      {
        name: "Machine Learning",
        description: "Deep expertise in ML models including ANN, Random Forests, BooST, and SVM",
        logo: "/icons/machine-learning.svg"
      },
      {
        name: "Python",
        description: "Advanced ML development, data processing, and automation",
        logo: "/icons/python.svg"
      },
      {
        name: "Data Engineering",
        description: "ETL pipelines, data integration, and distributed computing",
        logo: "/icons/data-engineering.svg"
      },
      {
        name: "MLOps",
        description: "ML/LLM lifecycle management with MLflow and SageMaker, drift monitoring (RAGAS), and deployment",
        logo: "/icons/mlops.svg"
      },
      {
        name: "Deep Learning",
        description: "TensorFlow, PyTorch, Hugging Face Transformers",
        logo: "/icons/deep-learning.svg"
      },
      {
        name: "LLM & NLP",
        description: "RAG pipelines, agentic workflows, LLM evaluation, LangChain, spaCy",
        logo: "/icons/nlp.svg"
      }
    ],
    domain: [
      {
        name: "Healthcare",
        description: "Data management utilities and ML solutions for healthcare",
        logo: "/icons/healthcare.svg"
      },
      {
        name: "Pipeline Integrity",
        description: "Corrosion prediction and structural analysis",
        logo: "/icons/pipeline.svg"
      },
      {
        name: "Process Control",
        description: "Advanced process control architecture and optimization",
        logo: "/icons/process.svg"
      },
      {
        name: "Sustainability",
        description: "Energy efficiency and decarbonization initiatives",
        logo: "/icons/sustainability.svg"
      },
      {
        name: "Waste Management",
        description: "Route optimization and IoT sensor data analysis",
        logo: "/icons/waste.svg"
      },
      {
        name: "Research",
        description: "Published research in ML and engineering applications",
        logo: "/icons/research.svg"
      }
    ],
    tools: [
      {
        name: "Cloud Platforms",
        description: "AWS (SageMaker, Bedrock), Azure (Azure OpenAI), Databricks",
        logo: "/icons/cloud.svg"
      },
      {
        name: "Docker & K8s",
        description: "Container orchestration for ML deployments",
        logo: "/icons/docker.svg"
      },
      {
        name: "Data Tools",
        description: "SSIS, Evidently, Great Expectations",
        logo: "/icons/data-tools.svg"
      },
      {
        name: "Version Control",
        description: "Git, GitHub, and CI/CD pipelines",
        logo: "/icons/git.svg"
      },
      {
        name: "Test Suites",
        description: "Unit tests, Integration tests, pytest",
        logo: "/icons/testing.svg"
      },
      {
        name: "Infrastructure",
        description: "Terraform, Datadog observability, cloud infrastructure management",
        logo: "/icons/infrastructure.svg"
      }
    ],
    qualifications: [
      {
        name: "P.Eng",
        description: "Professional Engineer Certification",
        logo: "/icons/engineering.svg"
      },
      {
        name: "PMP",
        description: "Project Management Professional",
        logo: "/icons/pmp.svg"
      },
      {
        name: "PMI-PBA",
        description: "Professional in Business Analysis",
        logo: "/icons/business-analysis.svg"
      },
      {
        name: "HSE-OSHA",
        description: "Health, Safety and Environment",
        logo: "/icons/safety.svg"
      },
      {
        name: "Publications",
        description: "Multiple published research papers",
        logo: "/icons/publications.svg"
      },
      {
        name: "Languages",
        description: "English, Igbo, Pidgin, Mandarin-(Beginner)",
        logo: "/icons/languages.svg"
      }
    ],
    softSkills: [
      {
        name: "Technical Leadership",
        description: "Team & Project Leadership across technical initiatives",
        logo: "/icons/tech-leadership.svg"
      },
      {
        name: "Innovation",
        description: "AI/ML Research & Development leadership",
        logo: "/icons/innovation.svg"
      },
      {
        name: "Business Acumen",
        description: "Strategy & Stakeholder Management",
        logo: "/icons/business.svg"
      },
      {
        name: "Communication",
        description: "Technical & Executive Communication",
        logo: "/icons/communication.svg"
      },
      {
        name: "Mentorship",
        description: "Team Development & Growth",
        logo: "/icons/mentorship.svg"
      },
      {
        name: "Problem Solving",
        description: "Complex System Architecture",
        logo: "/icons/problem-solving.svg"
      }
    ]
  },

  // Projects Data
  projects: [
    {
      title: "Netflix-Style Portfolio",
      description: "A creative portfolio website inspired by Netflix's UI/UX, built with React and TypeScript",
      technologies: ["React", "TypeScript", "Tailwind CSS"],
      imageUrl: "PROJECT_IMAGE_URL",
      githubLink: "GITHUB_URL",
      liveLink: "LIVE_PROJECT_URL"
    }
    // Add more projects
  ],


  // Certifications
  certifications: [
    {
      title: "Professional Engineer (P.Eng)",
      provider: "EGBC, APEGA, NSE",
      date: "2020",
      credentialUrl: "#",
      type: "professional",
      importance: 95
    },
    {
      title: "Project Management Professional (PMP)",
      provider: "PMI",
      date: "2021",
      credentialUrl: "#",
      type: "professional",
      importance: 90
    },
    {
      title: "Professional in Business Analysis (PMI-PBA)",
      provider: "PMI",
      date: "2021",
      credentialUrl: "#",
      type: "professional",
      importance: 85
    },
    {
      title: "Azure Data Engineer Associate",
      provider: "Microsoft",
      date: "2022",
      credentialUrl: "#",
      type: "technical",
      importance: 92
    },
    {
      title: "Azure AI Engineer Associate",
      provider: "Microsoft",
      date: "2022",
      credentialUrl: "#",
      type: "technical",
      importance: 94
    },
    {
      title: "TensorFlow Developer Certificate",
      provider: "Google",
      date: "2022",
      credentialUrl: "#",
      type: "technical",
      importance: 88
    },
    {
      title: "Databricks Certified Data Engineer Professional",
      provider: "Databricks",
      date: "2023",
      credentialUrl: "#",
      type: "technical",
      importance: 93
    },
    {
      title: "Machine Learning Engineering for Production (MLOps)",
      provider: "Coursera",
      date: "2022",
      credentialUrl: "#",
      type: "course",
      importance: 87
    },
    {
      title: "Health, Safety and Environment (HSE) - OSHA",
      provider: "OSHA",
      date: "2021",
      credentialUrl: "#",
      type: "safety",
      importance: 75
    },
    {
      title: "Deep Learning Specialization",
      provider: "Coursera",
      date: "2021",
      credentialUrl: "#",
      type: "course",
      importance: 86
    },
    {
      title: "Natural Language Processing with Transformers",
      provider: "Udemy",
      date: "2023",
      credentialUrl: "#",
      type: "course",
      importance: 89
    },
    {
      title: "Advanced SQL for Data Engineers",
      provider: "DataCamp",
      date: "2022",
      credentialUrl: "#",
      type: "course",
      importance: 82
    }
  ],

  trending: [
    {
      title: "SinewsLink: Founder & Advisor",
      description: "Founder and advisor to SinewsLink, which builds production AI/ML and MLOps infrastructure for defence, energy, mining, oil & gas and local economies.",
      category: 'leadership',
      image: "/images/sinewslink.gif",
      link: "https://sinewslink.com",
      date: "2018 - Present"
    },
    {
      title: "SL Aegis",
      description: "Canada-first geospatial decision-support platform for wildfire, flood and earthquake response, combining live hazard feeds with communities, infrastructure and shelter capacity.",
      category: 'project',
      image: "/images/aegis.png",
      link: "https://aegis.sinewslink.com",
      date: "2026"
    },
    {
      title: "Loupe",
      description: "Engineering-impact analytics for GitHub repositories, with LLM-generated insights, tests, Docker and architecture decision records.",
      category: 'project',
      image: "/images/loupe.png",
      link: "https://github.com/Anyaoha/loupe",
      date: "2026"
    },
    {
      title: "Upcoming Publication: AI for Pipeline Integrity",
      description: "Holistic AI-based safety management for maintaining pipeline structural integrity in the oil and gas sector. Under review with the International Journal of Pressure Vessels and Piping.",
      category: 'publication',
      image: "https://picsum.photos/seed/aipaper/400/225",
      date: "Under review"
    }
  ],

  topRated: [
    {
      title: "Machine Learning at Scale",
      category: "publication",
      description: "Published research on advanced machine learning techniques for concrete strength prediction using boosting smooth transition regression trees (BooST).",
      impact: [
        "An Elsevier Peer Reviewed Published Paper",
        "Presented in International Society for Optics and Photonics",
        "Novel approach to concrete performance prediction",
        "Significant improvement in prediction accuracy in Engineering Applications"
      ],
      image: "https://picsum.photos/seed/ml-publication/800/500",
      link: "https://www.sciencedirect.com/science/article/abs/pii/S095006182031477X",
      rating: 98,
      year: "2020"
    },
    {
      title: "SinewsLink Tech Innovation",
      category: "project",
      description: "Founder and advisor to a tech company specializing in data engineering and ML solutions for community-based SMEs.",
      impact: [
        "Developed innovative app achieving significant market penetration",
        "Established partnerships with Databricks, Microsoft, Azure, and AWS",
        "Created scalable data processing pipelines and advanced ML models"
      ],
      image: "https://picsum.photos/seed/sinewslink/800/500",
      rating: 97,
      year: "2023"
    },
    {
      title: "Healthcare ML Pipeline Optimization",
      category: "project",
      description: "Engineered sophisticated healthcare data management system with advanced ML capabilities.",
      impact: [
        "20% improvement in data processing efficiency",
        "Implemented Evidently and Great Expectations for data drift detection",
        "Enhanced patient outcomes through data-driven insights"
      ],
      image: "https://picsum.photos/seed/healthcare/800/500",
      rating: 96,
      year: "2024"
    },
    {
      title: "Natural Classic Physique Champion",
      category: "achievement",
      description: "Demonstrated exceptional dedication and discipline in competitive bodybuilding.",
      impact: [
        "2023 Natural Canada Pro Qualifier Winner",
        "2023 Vancouver Natural Overall Winner: Men's Classic Physique",
        "2023 Vancouver Natural Pro Winner: Men's Classic Physique",
        "3x Gold Medalist in Natural Men's Classic Physique",
        "3x Gold Medalist in Natural Men's Physique"
      ],
      image: "https://picsum.photos/seed/goldmedal/800/500",
      rating: 99,
      year: "2023"
    },
    {
      title: "MLOps Excellence",
      category: "project",
      description: "Led implementation of enterprise-wide MLOps practices and infrastructure.",
      impact: [
        "Pioneered CI/CD pipelines for model deployment",
        "Implemented comprehensive test suites for fraud detection",
        "Optimized model performance in production environments"
      ],
      image: "https://picsum.photos/seed/mlops/800/500",
      rating: 95,
      year: "2023"
    },
    {
      title: "Healthcare Sector Impact",
      category: "project",
      description: "Transformed healthcare data management and ML infrastructure, delivering significant improvements in efficiency and patient outcomes.",
      impact: [
        "Developed sophisticated data management utilities with Python and SSIS",
        "Optimized data processing workflows achieving 20% efficiency gain",
        "Successfully integrated healthcare compliance and data privacy measures",
        "Enhanced collaboration between healthcare professionals and data teams"
      ],
      image: "https://picsum.photos/seed/healthcare-testimonial/800/500",
      rating: 97,
      year: "2024"
    }
  ]
};

export const timelineData: TimelineData = {
  work: [
    {
      title: "Director of AI Engineering",
      company: "Enterprise",
      location: "Canada",
      period: "",
      type: "work",
      technologies: ["AI Strategy", "LLMOps", "AI Governance", "Team Leadership"],
      achievements: [
        "Set the technical vision and roadmap for AI/ML capabilities across the organization",
        "Built and led a cross-functional AI engineering team, with hiring standards and mentorship programs",
        "Drove executive decisions on AI investments, build-vs-buy evaluations and vendor partnerships",
        "Established AI governance, responsible AI practices and model risk frameworks"
      ],
      icon: "/icons/tech-leadership.svg"
    },
    {
      title: "AI Engineer / LLMOps Architect",
      company: "Enterprise",
      location: "Canada",
      period: "",
      type: "work",
      technologies: ["OpenTelemetry", "Azure OpenAI", "Bedrock", "SageMaker", "MLflow", "Databricks", "Terraform", "Datadog"],
      achievements: [
        "Architected an org-wide, OpenTelemetry-native LLM observability platform: shared collector template with per-team overlays, gateway-enforced redaction, and routing to S3, Datadog, Langfuse and Databricks Delta",
        "Authored a canonical span-attribute standard on the OTel GenAI semantic conventions, adopted by three product teams and required by InfoSec; new-team onboarding cut from 5-10 engineer-weeks to under one week",
        "Designed an LLM Gateway with multi-provider routing, automatic failover and per-team token budgets",
        "Built LLM observability with Datadog and RAGAS drift detection, reaching 100% model visibility",
        "Reduced ML deployment time from 45 days to under 5 minutes with Terraform self-service scaffolding",
        "Built an MLflow-based model governance framework; owned 11 evidence documents for ISO 42001 certification (zero non-conformities)",
        "Secured executive buy-in with an ROI case of $650K+ annual savings; authored 16+ ADRs"
      ],
      icon: "/icons/mlops.svg"
    },
    {
      title: "Senior Machine Learning Engineer",
      company: "Enterprise",
      location: "Canada",
      period: "",
      type: "work",
      technologies: ["RAG", "PyTorch", "Hugging Face", "LangChain", "AWS", "Azure"],
      achievements: [
        "Designed and deployed production RAG pipelines for enterprise knowledge retrieval",
        "Built multi-step agentic workflows with function calling and tool use",
        "Architected LLM evaluation infrastructure with prompt versioning and LLM-as-a-judge",
        "Implemented drift detection and monitoring for production NLP and LLM systems"
      ],
      icon: "/icons/nlp.svg"
    },
    {
      title: "Machine Learning & Database Developer",
      company: "Healthcare Industry",
      location: "Canada",
      period: "",
      type: "work",
      technologies: ["Python", "SSIS", "Evidently", "Great Expectations"],
      achievements: [
        "Developed advanced data management utilities for data drift detection",
        "Engineered robust healthcare data pipelines",
        "Optimized data processing workflows by 20%",
        "Implemented healthcare data privacy standards"
      ],
      icon: "/icons/ml.svg"
    },
    {
      title: "Data & Machine Learning Engineer",
      company: "Energy Industry",
      location: "Canada",
      period: "",
      type: "work",
      technologies: ["MLOps", "CI/CD", "Git", "Azure", "AWS", "Databricks"],
      achievements: [
        "Led MLOps strategies and end-to-end ML model deployments",
        "Implemented comprehensive test suites for fraud detection",
        "Pioneered CI/CD pipelines for model deployment",
        "Designed scalable data processing pipelines"
      ],
      icon: "/icons/data.svg"
    },
    {
      title: "Data Scientist",
      company: "Tech Industry",
      location: "Canada",
      period: "",
      type: "work",
      technologies: ["Machine Learning", "Python", "Data Analysis"],
      achievements: [
        "Developed algorithms for pattern detection in operations",
        "Created predictive models for pipeline corrosion assessment",
        "Led cross-functional data-driven initiatives",
        "Implemented performance metrics for ML models"
      ],
      icon: "/icons/scientist.svg"
    },
    {
      title: "Data Engineer",
      company: "Renewable / Waste Management",
      location: "Canada",
      period: "",
      type: "work",
      technologies: ["IoT", "Machine Learning", "Git", "Route Optimization"],
      achievements: [
        "Optimized waste collection routes using IoT sensor data",
        "Developed dynamic routing models for resource optimization",
        "Implemented Dijkstra's algorithm for schedule planning",
        "Managed code revision control systems"
      ],
      icon: "/icons/engineer.svg"
    },
    {
      title: "Structural Engineer",
      company: "Construction / Real Estate Industry",
      location: "Nigeria",
      period: "",
      type: "work",
      technologies: ["FEA", "CAD", "Structural Analysis"],
      achievements: [
        "Designed sustainable power houses for carbon footprint reduction",
        "Conducted structural analysis for commercial buildings",
        "Implemented innovative structural designs",
        "Supervised construction activities and contractors"
      ],
      icon: "/icons/structural.svg"
    }
  ],
  education: [
    {
      title: "Master of Applied Science in Engineering",
      company: "University of British Columbia",
      location: "British Columbia, Canada",
      period: "2017 - 2019",
      type: "education",
      technologies: ["Machine Learning", "Data Science", "Predictive Analysis"],
      achievements: [
        "Focused on Data Science applications",
        "Developed ML models for pipeline corrosion prediction",
        "Created ML models using ANN, RF, BooST, and SVM",
        "Research in concrete strength estimation"
      ],
      icon: "/icons/masters.svg"
    },
    {
      title: "Bachelor of Applied Science in Civil Engineering",
      company: "Nnamdi Azikiwe University",
      location: "Awka, Nigeria",
      period: "2009 - 2013",
      type: "education",
      technologies: ["Engineering", "Structural Analysis", "Design"],
      achievements: [
        "Foundation in engineering principles",
        "Structural analysis and design",
        "Project management fundamentals",
        "Technical communication"
      ],
      icon: "/icons/bachelor.svg"
    }
  ]
};

export default portfolioData; 